<template>
  <div>
    <h2 class="text-base font-bold mb-3">
      Configure your HR architecture
    </h2>
    <p class="text-sm leading-relaxed mb-6">
      Select every option that reflects how your organisation actually
      operates. Your selections here — not your organisation type —
      determine your HR architecture. These settings cannot be changed
      after setup without a data migration.
    </p>

    <p
      style="border-color: #E5E7EB;"
      class="text-xs font-black border-t border-b py-3 tracking-wide text-gray-800 uppercase mb-4"
    >
      Group 1 — Job classification
    </p>

    <div
      class="flex gap-1 p-3 rounded-xl mb-4 job-classification-info"
      role="status"
    >
      <span
        class="flex-shrink-0 w-8 h-8 flex items-center justify-center"
        aria-hidden="true"
      >
        <Icon icon-name="icon-info" class="text-darkPurple" size="xs" />
      </span>
      <p class="text-sm leading-snug m-0 pt-1">
        Job Family and Job Cadre cannot both be active. Select one.
      </p>
    </div>

    <div class="space-y-3">
      <OptionCardCheckbox
        :checked="selections.jobFamily"
        :disabled="false"
        title="Job family"
        description="Groups roles by functional discipline (Finance, Engineering, Operations). Typically private sector."
        @toggle="onOptionToggle('jobFamily', $event)"
      />

      <OptionCardCheckbox
        :checked="selections.jobCadre"
        :disabled="false"
        title="Job cadre"
        description="Classifies posts by employment class (Administrative, Technical, Professional). Replaces Job Family. Typically public sector, universities, NGOs."
        @toggle="onOptionToggle('jobCadre', $event)"
      />

      <OptionCardCheckbox
        :checked="selections.schemeOfService"
        :disabled="!selections.jobCadre"
        title="Scheme of service"
        description="Governing document for entry qualifications, salary scale references, and promotion criteria per cadre. Requires Job Cadre."
        @toggle="onOptionToggle('schemeOfService', $event)"
      />
    </div>

    <p
      style="border-color: #e5e7eb"
      class="text-xs font-black border-t border-b py-3 tracking-wide text-gray-800 uppercase mb-4 mt-8"
    >
      Group 2 — Job levels &amp; progression
    </p>

    <div class="space-y-3">
      <OptionCardCheckbox
        :checked="selections.jobLevel"
        :disabled="jobLevelDisabled"
        :showSelectedWhenDisabled="selections.jobFamily || selections.jobCadre"
        title="Job level"
        description="Seniority tiers attached to roles (L1, L2, Senior, Principal). Attaches to the role, not the employee. Required for range-based compensation."
        @toggle="onOptionToggle('jobLevel', $event)"
      />

      <OptionCardCheckbox
        :checked="selections.jobGrade"
        :disabled="jobGradeDisabled"
        :showSelectedWhenDisabled="selections.jobFamily || selections.jobCadre"
        title="Job grade"
        description="Sub-divisions within levels intra-level progression without promotion (L3-A → L3-B). Enables 3-tier model."
        @toggle="onOptionToggle('jobGrade', $event)"
      />

      <OptionCardCheckbox
        :checked="selections.gradeLevel"
        :disabled="gradeLevelDisabled"
        :showSelectedWhenDisabled="selections.jobCadre"
        title="Grade level"
        description="Seniority classification that attaches to the employee — not the role. Travels with the employee across posts. GL/Step public sector structure."
        @toggle="onOptionToggle('gradeLevel', $event)"
      />

      <OptionCardCheckbox
        :checked="selections.gradeLevel && selections.steps"
        :disabled="stepsCheckboxDisabled"
        :showSelectedWhenDisabled="true"
        title="Steps"
        description="Sub-divisions within grade levels. Progression is automatic — one step per year of satisfactory service. Requires Grade Level."
        @toggle="onOptionToggle('steps', $event)"
      >
        <template #title-extra>
          <span
            v-if="selections.gradeLevel && selections.steps"
            class="steps-auto-badge inline-flex items-center px-2 py-0.5 rounded-xl font-black uppercase tracking-wide"
          >
            Auto
          </span>
        </template>
      </OptionCardCheckbox>
    </div>

    <p
      style="border-color: #e5e7eb"
      class="text-xs font-black border-t border-b py-3 tracking-wide text-gray-800 uppercase mb-4 mt-8"
    >
      Group 3 — Compensation model
    </p>

    <div class="space-y-3">
      <OptionCardCheckbox
        :checked="selections.rangeBasedCompensation"
        :disabled="rangeBasedDisabled"
        title="Range-based compensation"
        description="Employees positioned within a pay band (min/mid/max). Compa-ratio is salary ÷ midpoint. Requires Job Level. Typical private sector."
        @toggle="onOptionToggle('rangeBasedCompensation', $event)"
      />

      <OptionCardCheckbox
        :checked="selections.scaleBasedCompensation"
        :disabled="scaleBasedDisabled"
        :showSelectedWhenDisabled="selections.jobCadre"
        title="Scale-based compensation"
        description="Employees receive a single exact salary — no band, no compa-ratio. For public sector: GL+Step → exact salary. For private: level/role → fixed schedule."
        @toggle="onOptionToggle('scaleBasedCompensation', $event)"
      />
    </div>
  </div>
</template>

<script>
import Icon from "@/components/Icon";
import OptionCardCheckbox from "./OptionCardCheckbox";

export default {
  name: "OrgOnboardingStep1",
  components: { Icon, OptionCardCheckbox },
  props: {
    selections: {
      type: Object,
      required: true,
    },
    jobLevelDisabled: {
      type: Boolean,
      default: false,
    },
    jobGradeDisabled: {
      type: Boolean,
      default: false,
    },
    gradeLevelDisabled: {
      type: Boolean,
      default: false,
    },
    stepsCheckboxDisabled: {
      type: Boolean,
      default: true,
    },
    rangeBasedDisabled: {
      type: Boolean,
      default: false,
    },
    scaleBasedDisabled: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    onOptionToggle(key, value) {
      this.$emit("option-toggle", { key, value });
    },
  },
};
</script>

<style scoped>
.job-classification-info {
  background: #fff5f0;
  border: 1px solid #fde4d7;
}

.steps-auto-badge {
  background: #0cb24c14;
  color: #0cb24c;
  font-size: 10px;
}
</style>

